import { Divider, Form, Input, InputNumber, Switch } from 'antd';
import React, { useMemo, useState } from 'react';
import { formValidatorRules, parseDescription, formatDescriptionKey } from '../../../../../common/utils';
import ModalComponent from '../../../../../components/ModalComponent';


const { Item } = Form;
const { required } = formValidatorRules;

const ConfigToyModal = ({
  isModalOpen,
  setIsModalOpen,
  onFinish,
  loadings,
  form,
  item,
  addScopeLabel,
  onClose,
  allDescriptionKeys = [],
}) => {
  const [isValidateTrigger, setIsValidateTrigger] = useState(false);

  // Parse only the current item's description to seed the initial field values
  const parsedDesc = useMemo(
    () => (item?.id ? parseDescription(item?.description) || {} : {}),
    [item],
  );

  // Populate all fields when the modal opens or the item changes.
  // Uses allDescriptionKeys so every known key appears even if missing in this record.
  React.useEffect(() => {
    if (!isModalOpen) return;

    if (item?.id) {
      const descFields = {};
      allDescriptionKeys.forEach(({ key }) => {
        descFields[`descField_${key}`] = parsedDesc[key];
      });

      form?.setFieldsValue({
        name: item?.itemKey,
        buttonLabel: item?.value,
        description: item?.description,
        ...descFields,
      });
    } else {
      form?.resetFields();
    }
  }, [form, isModalOpen, item, parsedDesc, allDescriptionKeys]);

  // True two-way sync: individual field ↔ description textarea
  const handleValuesChange = (changedValues) => {
    // Textarea edited → parse JSON and push values back into individual fields
    if ('description' in changedValues) {
      const parsed = parseDescription(changedValues.description);
      if (!parsed) return;
      const descFields = {};
      allDescriptionKeys.forEach(({ key }) => {
        if (key in parsed) descFields[`descField_${key}`] = parsed[key];
      });
      if (Object.keys(descFields).length > 0) form.setFieldsValue(descFields);
      return;
    }

    // Individual field edited → rebuild JSON and sync to textarea
    const hasDescFieldChange = Object.keys(changedValues).some((k) =>
      k.startsWith('descField_'),
    );
    if (!hasDescFieldChange) return;

    const allValues = form.getFieldsValue();

    // Start from the current textarea value so any extra keys the user manually
    // added (not in allDescriptionKeys) are preserved
    const descObj = parseDescription(allValues.description) || {};

    // Sync individual field values — delete the key when the field is cleared
    allDescriptionKeys.forEach(({ key, type }) => {
      const val = allValues[`descField_${key}`];
      const isEmpty =
        val === undefined ||
        val === null ||
        (type === 'string' && val === '');
      if (isEmpty) {
        delete descObj[key];
        return;
      }
      if (type === 'boolean') descObj[key] = Boolean(val);
      else if (type === 'number') descObj[key] = Number(val);
      else descObj[key] = val;
    });
    form.setFieldValue('description', JSON.stringify(descObj));
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form?.resetFields();
    onClose?.();
  };

  // Description textarea is always the source of truth on save
  const handleFormFinish = (values) => {
    onFinish({
      name: values.name,
      buttonLabel: values.buttonLabel,
      description: values.description || '',
    });
    setIsValidateTrigger(true);
  };

  const renderDescriptionField = (key, type) => {
    if (type === 'boolean') return <Switch />;
    if (type === 'number') return <InputNumber style={{ width: '100%' }} />;
    return <Input allowClear placeholder={`Enter ${formatDescriptionKey(key)}`} />;
  };

  let modalTitle = 'Add New Item';
  if (item?.id) modalTitle = 'Edit Item';
  else if (addScopeLabel) modalTitle = `Add ${addScopeLabel} Item`;

  return (
    <ModalComponent
      open={isModalOpen}
      onCancel={handleCancel}
      onOk={form?.submit}
      title={modalTitle}
      okText="Save"
      confirmLoading={loadings}
      destroyOnClose
      forceRender
      maskClosable={false}
    >
      <Divider />
      <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
        <Form
          form={form}
          onFinish={handleFormFinish}
          onValuesChange={handleValuesChange}
          autoComplete="off"
          layout="vertical"
          validateTrigger={isValidateTrigger ? 'onChange' : 'onSubmit'}
          onFinishFailed={() => setIsValidateTrigger(true)}
        >
          <Item
            label="Name"
            name="name"
            rules={[{ ...required, message: 'Please Enter Name!' }]}
          >
            <Input allowClear placeholder="Enter Name" maxLength={100} />
          </Item>

          <Item
            label="Button Label"
            name="buttonLabel"
            rules={[{ ...required, message: 'Please Enter Button Label!' }]}
          >
            <Input allowClear placeholder="Enter Button Label" maxLength={100} />
          </Item>

          {allDescriptionKeys.map(({ key, type }) => (
            <Item
              key={key}
              label={formatDescriptionKey(key)}
              name={`descField_${key}`}
              valuePropName={type === 'boolean' ? 'checked' : 'value'}
            >
              {renderDescriptionField(key, type)}
            </Item>
          ))}

          <Item
            label="Description"
            name="description"
            rules={[{ ...required, message: 'Please enter a description!' }]}
          >
            <Input.TextArea
              rows={4}
              allowClear
              placeholder="Enter Description"
              maxLength={800}
            />
          </Item>
        </Form>
      </div>
    </ModalComponent>
  );
};

export default ConfigToyModal;
